import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableContainer,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  CircularProgress,
  Link,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate, useLocation } from "react-router-dom";

const headCellSx = {
  bgcolor: "#1565C0",
  color: "#fff",
  fontWeight: 700,
  whiteSpace: "nowrap",
  py: 1.15,
  fontSize: 14,
};

const actionButtonSx = {
  width: 44,
  height: 38,
  borderRadius: 1.6,
  border: "1px solid #dbeafe",
  bgcolor: "#f8fbff",
  color: "#1565C0",
  "&:hover": {
    bgcolor: "#eef6ff",
  },
};

const getAccessToken = () =>
  localStorage.getItem("access") ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") ||
  "";

const authHeaders = (json = false) => {
  const token = getAccessToken();
  const headers = {};

  if (json) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;

  return headers;
};

const AdminProforma = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [proformas, setProformas] = useState([]);
  const [filteredProformas, setFilteredProformas] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [successMessage, setSuccessMessage] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadText, setDownloadText] = useState("Descargando proforma...");
  const [downloadError, setDownloadError] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchProformas = useCallback(() => {
    setLoading(true);

    fetch("/proformas/listar-proformas/", {
      method: "GET",
      headers: authHeaders(),
    })
      .then((resp) => {
        if (resp.status === 401 || resp.status === 403) {
          localStorage.clear();
          navigate("/login");
          return null;
        }
        return resp.json();
      })
      .then((data) => {
        if (!data) return;
        setProformas(Array.isArray(data) ? data : []);
        setFilteredProformas(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setProformas([]);
        setFilteredProformas([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  useEffect(() => {
    const token = getAccessToken();
    const isSuper = localStorage.getItem("is_superuser") === "true";
    const isAdmin = localStorage.getItem("is_admin") === "true";
    const isVendedor = localStorage.getItem("is_vendedor") === "true";

    if (!token || (!isSuper && !isAdmin && !isVendedor)) {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    fetchProformas();
  }, [fetchProformas]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchProformas();
    };

    document.addEventListener("visibilitychange", onVisible);

    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchProformas]);

  useEffect(() => {
    if (location.state && location.state.created) {
      setSuccessMessage("Proforma creada con éxito.");
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleDownload = (proforma) => {
    setDownloadError("");
    setDownloadText(`Descargando ${proforma.identificacion_proforma}...`);
    setDownloadOpen(true);

    const url = `/proformas/descargar-proforma/${proforma.id}/?t=${Date.now()}`;

    fetch(url, {
      method: "GET",
      headers: authHeaders(),
    })
      .then((resp) => {
        if (resp.status === 401 || resp.status === 403) {
          localStorage.clear();
          navigate("/login");
          return null;
        }

        if (!resp.ok) {
          throw new Error("No se pudo descargar la proforma.");
        }

        return resp.blob();
      })
      .then((blob) => {
        if (!blob) return;

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `Proforma_${proforma.identificacion_proforma}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setDownloadOpen(false);
      })
      .catch(() => {
        setDownloadOpen(false);
        setDownloadError("Error al descargar la proforma. Intente nuevamente.");
      });
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);

    const filtered = proformas.filter((p) => {
      const id = `${p.identificacion_proforma || ""}`.toLowerCase();
      const cliente = `${p.cliente || ""}`.toLowerCase();
      const contacto = `${p.contacto || ""}`.toLowerCase();
      const elaborado = `${p.elaborado_por || ""}`.toLowerCase();
      const estado = `${p.estado_proforma || ""}`.toLowerCase();
      const fecha = `${p.fecha || ""}`.toLowerCase();

      return (
        id.includes(value) ||
        cliente.includes(value) ||
        contacto.includes(value) ||
        elaborado.includes(value) ||
        estado.includes(value) ||
        fecha.includes(value)
      );
    });

    setFilteredProformas(filtered);
    setPage(0);
  };

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminServices") navigate("/admin-panel/servicios");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", {
        method: "POST",
        headers: authHeaders(),
      });
    } catch {}

    localStorage.clear();
    navigate("/login");
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleSelect={handleSelect}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} setSelectedComponent={() => navigate("/admin-panel")} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3, gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                textTransform: "none",
                backgroundColor: "#1565C0",
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
                "&:hover": { backgroundColor: "#0D47A1" },
              }}
              onClick={() => navigate("/admin-panel/proformas/nueva")}
            >
              Nueva Proforma
            </Button>
          </Box>

          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={2}
              mb={2}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 19, color: "#0f172a" }}>
                  Proformas
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
                  Gestión de proformas registradas y descarga de documentos
                </Typography>
              </Box>

              <Chip
                label={`${filteredProformas.length} registros`}
                color="info"
                variant="outlined"
                sx={{ fontWeight: 700, borderRadius: 2 }}
              />
            </Stack>

            <TextField
              fullWidth
              placeholder="Buscar proforma"
              value={search}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2.2 }}
            />

            <Box
              sx={{
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                overflow: "hidden",
                bgcolor: "#fff",
              }}
            >
              <TableContainer
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  maxWidth: "100%",
                  "&::-webkit-scrollbar": { height: 10 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#cbd5e1",
                    borderRadius: 10,
                  },
                }}
              >
                <Table size="small" sx={{ minWidth: 1180 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headCellSx, minWidth: 150 }}>ID</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 260 }}>Cliente</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 150 }}>Estado</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 140 }}>Fecha</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 220 }}>Elaborado por</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 170 }} align="center">
                        Descargar
                      </TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 150 }} align="center">
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={26} />
                        </TableCell>
                      </TableRow>
                    ) : filteredProformas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                          <Stack spacing={0.75} alignItems="center">
                            <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                              No hay proformas para mostrar
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                              Ajusta la búsqueda para encontrar resultados
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredProformas
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((p) => (
                          <TableRow key={p.id} hover>
                            <TableCell>
                              <Link
                                component="button"
                                underline="hover"
                                sx={{ fontSize: 13, fontWeight: 700, color: "#1565C0" }}
                                onClick={() => navigate(`/admin-panel/proformas/facturacion/${p.id}`)}
                              >
                                {p.identificacion_proforma}
                              </Link>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: 13.5,
                                  fontWeight: 800,
                                  color: "#0f172a",
                                  textTransform: "uppercase",
                                }}
                              >
                                {p.cliente || "-"}
                              </Typography>

                              {p.contacto && (
                                <Typography
                                  sx={{
                                    fontSize: 12.5,
                                    color: "#64748b",
                                    fontWeight: 600,
                                  }}
                                >
                                  / {p.contacto}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={p.estado_proforma || "-"}
                                size="small"
                                sx={{
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  bgcolor: "#ececec",
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Typography sx={{ fontSize: 13 }}>{p.fecha || "-"}</Typography>
                            </TableCell>

                            <TableCell>
                              <Typography sx={{ fontSize: 13 }}>{p.elaborado_por || "-"}</Typography>
                            </TableCell>

                            <TableCell align="center">
                              <Tooltip title="Descargar">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownload(p)}
                                  sx={actionButtonSx}
                                >
                                  <DownloadOutlinedIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>

                            <TableCell align="center">
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/admin-panel/proformas/editar/${p.id}`)}
                                  sx={actionButtonSx}
                                >
                                  <EditOutlinedIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filteredProformas.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[20]}
                labelRowsPerPage="Filas por página"
              />
            </Box>
          </Paper>
        </Box>
      </Box>

      <Dialog open={downloadOpen}>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CircularProgress size={26} />
            <Box>
              <Typography sx={{ fontWeight: 700 }}>{downloadText}</Typography>
              <Typography sx={{ fontSize: 13 }}>
                Por favor espere, no cierre esta ventana.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>

      <Snackbar
        open={!!downloadError}
        autoHideDuration={3500}
        onClose={() => setDownloadError("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{downloadError}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminProforma;